// The version-history list — maps snapshots → rows (label, author, date) with
// an ENABLED "Voir cette version" affordance: clicking it opens the in-pane
// read-only preview (F-VERSIONS-PREVIEW) via the `onView` callback the pane
// lifts. Pure presentation: the filter+sort projection already happened in the
// adapter (W1); the preview fetch + render lives in the pane / SectionRenderer.
//
//   §9 — logical RTL utilities; BE `snapshotLabel` + `authorName` get
//        `bidiProps` (they can be Arabic in an FR chrome). The date is
//        formatted with `Intl.DateTimeFormat(i18n.language)` — locale-aware,
//        not concatenated.
//   W6 — no $/tokens: rows show label/author/date only.

import { useTranslation } from 'react-i18next'
import { bidiProps } from '@/lib/bidi'
import type { VersionSnapshot } from '@/domain/versions/entity'

interface VersionHistoryListProps {
  snapshots: VersionSnapshot[]
  /** Open the in-pane read-only preview of a snapshot (by its écriture id). */
  onView: (snapshotId: string) => void
}

/** Locale-aware date+time formatting. Invalid/empty ISO → empty string (the
 *  row still renders its label/author). Presentation only. */
function formatDate(iso: string, language: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(language || 'fr', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function VersionHistoryList({
  snapshots,
  onView,
}: VersionHistoryListProps): JSX.Element {
  const { t, i18n } = useTranslation()

  if (snapshots.length === 0) {
    return (
      <div
        data-testid="versions-history-empty"
        className="px-3.5 py-6 text-center text-xs leading-relaxed text-ink/60"
      >
        {t('versions.empty')}
      </div>
    )
  }

  return (
    <ul data-testid="versions-history-list" className="flex flex-col">
      {snapshots.map((snap) => (
        <VersionRow
          key={snap.id}
          snapshot={snap}
          language={i18n.language}
          onView={onView}
        />
      ))}
    </ul>
  )
}

interface VersionRowProps {
  snapshot: VersionSnapshot
  language: string
  onView: (snapshotId: string) => void
}

function VersionRow({
  snapshot,
  language,
  onView,
}: VersionRowProps): JSX.Element {
  const { t } = useTranslation()
  const label = snapshot.snapshotLabel ?? t('versions.unlabeled')
  const date = formatDate(snapshot.createdAt, language)

  return (
    <li
      data-testid={`versions-history-row-${snapshot.id}`}
      className="flex items-start gap-2.5 border-b border-ink/[0.06] px-3.5 py-2.5"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className="truncate text-xs font-semibold text-ink"
          {...bidiProps(label)}
        >
          {label}
        </span>
        <span className="mt-0.5 truncate text-[10px] text-ink/60">
          {t('versions.row.meta', {
            version: snapshot.version,
            author: snapshot.authorName,
            date,
          })}
        </span>
        <span className="sr-only" {...bidiProps(snapshot.authorName)}>
          {snapshot.authorName}
        </span>
      </div>
      {/* Opens the in-pane read-only preview (F-VERSIONS-PREVIEW). A snapshot
          IS itself a case-écriture, so its row id is the écriture id we fetch
          the detail for — no extra resolution (W1). */}
      <button
        type="button"
        data-testid={`versions-history-view-${snapshot.id}`}
        onClick={() => onView(snapshot.id)}
        title={t('versions.row.view')}
        className="shrink-0 rounded px-2 py-1 text-[10px] font-medium text-ink/70 hover:bg-ink/5"
      >
        {t('versions.row.view')}
      </button>
    </li>
  )
}
